// ============================================================================
// Typography Presets — M8 Wave 1
// ============================================================================

export interface TypeScaleEntry {
  label: string;
  px: number;
}

export const TYPE_SCALE: Record<string, TypeScaleEntry> = {
  xs: { label: 'Extra Small (12px)', px: 12 },
  sm: { label: 'Small (14px)', px: 14 },
  base: { label: 'Base (16px)', px: 16 },
  lg: { label: 'Large (18px)', px: 18 },
  xl: { label: 'XL (20px)', px: 20 },
  '2xl': { label: '2XL (24px)', px: 24 },
  '3xl': { label: '3XL (30px)', px: 30 },
  '4xl': { label: '4XL (36px)', px: 36 },
  '5xl': { label: '5XL (48px)', px: 48 },
  '6xl': { label: '6XL (60px)', px: 60 },
  '7xl': { label: '7XL (72px)', px: 72 },
};

export interface FontPair {
  id: string;
  label: string;
  heading: string;
  body: string;
  googleUrl: string;
}

export const FONT_PAIRS: FontPair[] = [
  {
    id: 'inter-source-serif',
    label: 'Inter + Source Serif',
    heading: '"Inter", sans-serif',
    body: '"Source Serif 4", serif',
    googleUrl:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Source+Serif+4:wght@400;600&display=swap',
  },
  {
    id: 'space-grotesk-lora',
    label: 'Space Grotesk + Lora',
    heading: '"Space Grotesk", sans-serif',
    body: '"Lora", serif',
    googleUrl:
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Lora:wght@400;600&display=swap',
  },
  {
    id: 'poppins-nunito',
    label: 'Poppins + Nunito',
    heading: '"Poppins", sans-serif',
    body: '"Nunito", sans-serif',
    googleUrl:
      'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Nunito:wght@400;600&display=swap',
  },
  {
    id: 'playfair-raleway',
    label: 'Playfair Display + Raleway',
    heading: '"Playfair Display", serif',
    body: '"Raleway", sans-serif',
    googleUrl:
      'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Raleway:wght@400;600&display=swap',
  },
  {
    id: 'dm-sans-dm-serif',
    label: 'DM Sans + DM Serif Display',
    heading: '"DM Serif Display", serif',
    body: '"DM Sans", sans-serif',
    googleUrl:
      'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=DM+Serif+Display&display=swap',
  },
];

export const LINE_HEIGHT_VALUES: Record<string, number> = {
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.8,
};

export const LETTER_SPACING_VALUES: Record<string, string> = {
  tighter: '-0.04em',
  tight: '-0.02em',
  normal: '0',
  wide: '0.05em',
  wider: '0.1em',
  widest: '0.2em',
  ultra: '0.3em',
};

export const AVAILABLE_FONTS: Array<{ id: string; label: string; family: string; source: 'system' | 'pair' }> = [
  // System-loaded (always available via index.html)
  { id: 'roboto', label: 'Roboto', family: '"Roboto", sans-serif', source: 'system' },
  { id: 'bricolage-grotesque', label: 'Bricolage Grotesque', family: '"Bricolage Grotesque", sans-serif', source: 'system' },
  { id: 'roboto-mono', label: 'Roboto Mono', family: '"Roboto Mono", monospace', source: 'system' },
  // From font pairs (dynamically loaded)
  { id: 'inter', label: 'Inter', family: '"Inter", sans-serif', source: 'pair' },
  { id: 'source-serif-4', label: 'Source Serif 4', family: '"Source Serif 4", serif', source: 'pair' },
  { id: 'space-grotesk', label: 'Space Grotesk', family: '"Space Grotesk", sans-serif', source: 'pair' },
  { id: 'lora', label: 'Lora', family: '"Lora", serif', source: 'pair' },
  { id: 'poppins', label: 'Poppins', family: '"Poppins", sans-serif', source: 'pair' },
  { id: 'nunito', label: 'Nunito', family: '"Nunito", sans-serif', source: 'pair' },
  { id: 'playfair-display', label: 'Playfair Display', family: '"Playfair Display", serif', source: 'pair' },
  { id: 'raleway', label: 'Raleway', family: '"Raleway", sans-serif', source: 'pair' },
  { id: 'dm-sans', label: 'DM Sans', family: '"DM Sans", sans-serif', source: 'pair' },
  { id: 'dm-serif-display', label: 'DM Serif Display', family: '"DM Serif Display", serif', source: 'pair' },
];

/**
 * Get CSS font-family string by font id.
 * Returns undefined if not found.
 */
export function getFontFamilyById(id: string | undefined): string | undefined {
  if (!id) return undefined;
  return AVAILABLE_FONTS.find((f) => f.id === id)?.family;
}

export const MOBILE_BREAKPOINT = 768;
